// THE WALLET REGISTER'S FOCUSED VIEW (UI rebuild batch 5) — one address over the register (kit DetailSheet: a panel at the right from lg, the whole
// screen on the phone), or one Ledger account key's addresses. Astra: "Add or retire an address in a focused view"; "retirement reason until
// requested". The steps and their forms are Screens' round-6 reformat (branch screens/wallet-register, 538a2ff), moved here unchanged in what they do:
//
//   · the form is the SCREEN's, by address id (view.ts openForm/editForm/failForm/closeForm) — StepPanel holds no state, so closing the sheet, a filter
//     or the window crossing the laptop breakpoint loses no draft, no request in flight and no failure; ONE sheet is mounted, so a row has at most one
//     form panel on the page (round 3's reason for drawing one layout at a time);
//   · a request's answer is read from what was ASKED (the mutation's variable), never from what is on screen when it lands;
//   · a row with a request in flight has its steps, its boxes and its buttons locked — nothing is typed while a request is out (round 4);
//   · a step the row no longer offers, or a typed label over one that moved, keeps its Cancel and cannot be submitted (rounds 4–5);
//   · Approve payout address is the owner's (the server says; the button is shown and not pressable for anyone else, with its reason in a few words);
//   · Archive is the red one and never the gold; the gold is the open form the operator works in (view.ts goldForm / goldCandidates).
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type RegisterWallet } from '../../../lib/api';
import { errorWords, readWords } from '../../../lib/errors';
import { fmtRelative } from '../../../lib/format';
import { tg } from '../../../lib/telegram';
import { stepWords, watchWords } from '../../../lib/walletStates';
import { treasuryHref } from '../../../lib/ownAccounts';
import { Pill } from '../../Pill';
import { Field } from '../../Field';
import { ErrorLine } from '../../ErrorLine';
import { CopyRef } from '../../CopyRef';
import { DetailSheet, Fact, StateLine, TAP } from '../kit';
import {
  STEP_WORD, addressCount, foldName, labelConflict, nameOf, rowActions, rowFacts, shownLabel, stepLine, stepStillOffered, walletNameOf,
  type Form, type Forms, type PayoutFacts, type RegisterGroup, type Step,
} from './view';

/** What the sheet is handed. The forms live in the SCREEN, by address id, not here (Astra, 26 Oct). */
export interface Ctx {
  canChange: boolean;
  canApprovePayouts: boolean;
  forms: Forms;
  /** Open `step` on the row — or close the row's form when that step is the open one. */
  toggle: (w: RegisterWallet, step: Step) => void;
  /** What was typed; `live` is the row's label as it stands (a label draft begins from it). */
  edit: (id: string, patch: Partial<Pick<Form, 'reason' | 'label'>>, live?: string) => void;
  /** Which open form owns the screen's one gold button (goldForm), and the form the operator just worked in. */
  gold: string | null;
  touch: (key: string) => void;
  /** The failure a request answered (null clears it), kept in the row's form. */
  fail: (id: string, words: string | null) => void;
  /** Close the row's form — only if it is still the step that was SUBMITTED. */
  close: (id: string, step: Step) => void;
  /** The register's read is known: its counts may be said (a failed refresh keeps its rows and claims nothing from them). */
  known: boolean;
  /** Rows with a request in flight: their steps are locked and they stay in the list. */
  pending: Record<string, true>;
  setPending: (id: string, on: boolean) => void;
  /** What a step reached beyond the Books, said at the top of the register; the sheet on that row closes so it is read. */
  onNotice: (words: string, id: string) => void;
}

/** The chain, as a small tag — the one thing every address has. */
export function ChainTag({ chain }: { chain: string }) {
  return <span className="shrink-0 rounded bg-panel-2 px-1.5 font-mono text-meta text-tg-hint">{chain}</span>;
}

/** An address's payout stage: one word in its tone, and the day it was approved when that is known. */
export function PayoutPill({ p, day = true }: { p: PayoutFacts; day?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span title={p.hint ?? undefined}><Pill tone={p.tone}>{p.word}</Pill></span>
      {day && p.when && <span className="font-mono tabular text-meta text-tg-hint">{p.when}</span>}
    </span>
  );
}

/** The party a row stands under, and the door to it: a desk wallet is read on Treasury (operator, 21 Oct 2026); any other party opens its page. */
function OwnerLine({ g, role }: { g: RegisterGroup; role: string }) {
  return (
    <p className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-meta text-tg-hint">
      <span className="min-w-0 break-words">{g.name} · {role}</span>
      {g.desk
        ? <Link to={treasuryHref(g.treasury)} className={`${TAP} whitespace-nowrap`}>On Treasury →</Link>
        : <Link to={`/parties/${encodeURIComponent(g.slug)}`} className={`${TAP} whitespace-nowrap`}>Party →</Link>}
    </p>
  );
}

/** The WHOLE address, wrapping — the register exists to check one address against another (design system §5's one exception) — and Copy. */
function WholeAddress({ value }: { value: string }) {
  return (
    <div className="rounded-lg border border-stroke-soft bg-panel-2 px-3 py-2">
      <p className="break-all font-mono text-body">{value}</p>
      <CopyRef value={value} label="Copy" className="mt-1 font-sans" />
    </div>
  );
}

export function WalletSheet({ w, g, role, ctx, onClose }: { w: RegisterWallet; g: RegisterGroup; role: string; ctx: Ctx; onClose: () => void }) {
  const f = rowFacts(w);
  const form = ctx.forms[w.id];
  const close = useCallback(() => onClose(), [onClose]);
  return (
    <DetailSheet title={nameOf(w)} onClose={close}>
      <div className="space-y-4">
        <OwnerLine g={g} role={role} />
        <WholeAddress value={w.address} />
        <div>
          <Fact label="Network">{w.chain}</Fact>
          <Fact label="State"><Pill tone={f.state.tone}>{f.state.word}</Pill></Fact>
          {f.payout && <Fact label="Payout"><PayoutPill p={f.payout} /></Fact>}
          <Fact label="Added">{f.added}</Fact>
          <Fact label="Last seen">{f.seen ?? '—'}</Fact>
          <Fact label="On chain"><span className="font-mono tabular">{f.txs ?? '—'}</span></Fact>
        </div>
        {f.note && <p className={`text-meta ${f.note.warn ? 'text-warn' : 'text-tg-hint'}`}>{f.note.text}{f.note.more && <span className="text-tg-hint"> · {f.note.more}</span>}</p>}
        {ctx.canChange && <RowSteps w={w} ctx={ctx} />}
        {form && <StepPanel w={w} form={form} ctx={ctx} />}
      </div>
    </DetailSheet>
  );
}

/** One Ledger account key's addresses: shown, never changed here (the key's scan keeps them). A desk account's key says its own state — the next
 *  receive address and when its branches were last scanned (it used to stand on the party page, which now opens Treasury). */
/** A run of addresses that stands as ONE register row: a Ledger account key's (its scan keeps them), or — `deposits` — the desk's
 *  per-transaction deposit addresses, one issued per deposit. */
export function KeySheet({ g, role, rows, known, onClose, deposits = false }: { g: RegisterGroup; role: string; rows: readonly RegisterWallet[]; known: boolean; onClose: () => void; deposits?: boolean }) {
  const close = useCallback(() => onClose(), [onClose]);
  return (
    <DetailSheet title={foldName(rows, deposits) ?? (deposits ? 'Deposit' : 'Ledger account key')} onClose={close}>
      <div className="space-y-4">
        <OwnerLine g={g} role={role} />
        <p className="text-meta text-tg-hint">{known ? addressCount(rows.length) : '— addresses'} · {deposits ? 'one issued for each deposit' : 'its scan keeps them'}</p>
        {g.desk && !deposits && <LedgerKeyLines slug={g.slug} />}
        <ul className="space-y-3">
          {rows.map((w) => <KeyAddress key={w.id} w={w} />)}
        </ul>
      </div>
    </DetailSheet>
  );
}

function KeyAddress({ w }: { w: RegisterWallet }) {
  const f = rowFacts(w);
  return (
    <li className="border-t border-stroke-soft pt-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <ChainTag chain={w.chain} />
        {(walletNameOf(w) ?? w.label) && <span className="min-w-0 break-words text-body font-medium">{walletNameOf(w) ?? w.label}</span>}
        <Pill tone={f.state.tone} className="ml-auto">{f.state.word}</Pill>
      </div>
      <p className="mt-1 break-all font-mono text-meta">{w.address}</p>
      <p className="mt-1 flex flex-wrap gap-x-3 text-meta text-tg-hint">
        {w.derivation && <span className="font-mono">{w.derivation}</span>}
        <span>Added {f.added}</span>
        {f.seen && <span>seen {f.seen}</span>}
        {f.txs != null && <span><span className="font-mono tabular">{f.txs}</span> on chain</span>}
        <CopyRef value={w.address} label="Copy" className="font-sans" />
      </p>
      {f.note && <p className={`mt-1 text-meta ${f.note.warn ? 'text-warn' : 'text-tg-hint'}`}>{f.note.text}</p>}
    </li>
  );
}

/** WHAT THE LEDGER ACCOUNT KEY IS DOING — one line per key (Screens' LedgerKeyLines, unchanged): the next receive address and when its branches were
 *  last scanned. The key itself never reaches a screen. The party read is the one Book trade already makes, under the same key. */
function LedgerKeyLines({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ['party', slug, 'balances'],
    queryFn: () => api.partyDetail(slug, 'dubaiotc'),
    staleTime: 60_000,
  });
  if (q.isError) return <StateLine tone="warn" more={readWords(q.error)}>Couldn&rsquo;t read the key&rsquo;s scan.</StateLine>;
  const keys = q.data?.wallet_keys ?? [];
  if (keys.length === 0) return null;
  return (
    <>
      {keys.map((k, i) => (
        <p key={i} className="text-meta text-tg-hint">
          Key scan · next receive 0/{k.last_receive_used + 1}
          {' · '}{k.last_scanned_at ? `scanned ${fmtRelative(k.last_scanned_at)}` : 'not scanned yet'}
        </p>
      ))}
    </>
  );
}

/** The steps a row offers; the open one reads Cancel. Always there for the open step — even when the row no longer offers it — so a form that holds a
 *  draft is always closable. Approve payout address is shown to everyone who can change the register, pressable by the owner only. */
function RowSteps({ w, ctx }: { w: RegisterWallet; ctx: Ctx }) {
  const offered = rowActions(w, { canChange: ctx.canChange, canApprovePayouts: ctx.canApprovePayouts });
  const open = ctx.forms[w.id]?.step;
  const acts = open && !offered.some((a) => a.step === open) ? [...offered, { step: open, word: STEP_WORD[open], disabled: false }] : offered;
  if (acts.length === 0) return null;
  const busy = !!ctx.pending[w.id];
  const ownerOnly = acts.some((a) => a.step === 'approve' && a.disabled);
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-2">
        {acts.map((a) => {
          const on = open === a.step;
          return (
            <button key={a.step} type="button" className="btn-secondary min-h-11 px-3 text-body disabled:opacity-50 lg:min-h-10" disabled={a.disabled || busy}
              title={a.disabled ? 'Only the owner approves payout addresses' : undefined}
              aria-expanded={on} onClick={() => ctx.toggle(w, a.step)}>
              {on ? 'Cancel' : a.word}
            </button>
          );
        })}
      </div>
      {ownerOnly && <p className="text-meta text-tg-hint">Only the owner approves</p>}
    </div>
  );
}

/** What a request was asked, captured when it was pressed: its answer is read from THIS, never from what is on screen when the answer arrives. */
interface Submit { step: Step; reason: string; label: string }

/** The open step's form: a line on what it does, a reason for the two that go one way, the one button. It holds NO state of its own — the draft, the
 *  failure and the request in flight are the screen's (ctx), keyed by the row. The mutations' own callbacks run whether or not the sheet is open. */
function StepPanel({ w, form, ctx }: { w: RegisterWallet; form: Form; ctx: Ctx }) {
  const qc = useQueryClient();
  const step = form.step;
  const busy = !!ctx.pending[w.id];
  // A step the row no longer offers cannot be submitted (the row changed under the form); the draft stays until it is closed. So is a typed label over a
  // label that has since moved: saving it would overwrite the newer one unseen. An untouched label draft just follows the row.
  const labelMoved = step === 'label' && labelConflict(form, w);
  const stale = !stepStillOffered(w, step) || labelMoved;
  const primary = ctx.gold === w.id;
  const name = nameOf(w);
  const change = useMutation({
    onMutate: () => { ctx.setPending(w.id, true); ctx.fail(w.id, null); },
    onSettled: () => ctx.setPending(w.id, false),
    mutationFn: (v: Submit) => api.changeWallet(w.id, v.step === 'label' ? { action: 'label', label: v.label } : { action: v.step as 'retire' | 'archive', reason: v.reason }),
    onSuccess: (r, v) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      // What the energy desk did (Energy's feed, read by routes/wallets.ts energyWord).
      const e = r.energy;
      const energyWords = !e ? ''
        : !e.told ? ` The energy desk could not be told (${e.reason}) — the ten-minute check tries again.`
        : 'to' in e ? ` The energy desk ${e.to === 'retired' ? 'retired it too' : 'paused it'}.`
        : ' The energy desk had nothing to change for it.';
      if (v.step === 'retire') ctx.onNotice(`${name} is retired — no longer handed out; ${watchWords('archived', w.chain)}.${energyWords}`, w.id);
      if (v.step === 'archive') {
        // The screening worker's own list (KYT's sentences, 6 Oct 2026). With the chain, always: the address alone matches an EVM key's twin on another
        // network, and the worker then removes nothing and asks (KYT, 63ccaea) — the chain names exactly one wallet.
        const k = r.kyt;
        const kytWords = !k ? ''
          : k.told ? (k.removed ? ' KYT stopped screening it.' : ' KYT was not screening it.')
          : ` KYT could not be told (${k.reason}) — send /unwatch ${w.chain} ${w.address} in the ops group.`;
        ctx.onNotice(`${name} is archived — the chain reader no longer reads it.${kytWords}${energyWords}`, w.id);
      }
      ctx.close(w.id, v.step);
      qc.invalidateQueries({ queryKey: ['wallets'] });
      qc.invalidateQueries({ queryKey: ['party', w.party_slug] });
    },
    onError: (e) => { tg()?.HapticFeedback.notificationOccurred('error'); ctx.fail(w.id, errorWords(e)); },
  });
  // A payout address is payable by the wallet only once the Books approve it (N-061, routes/wallets.ts): the owner alone, from his enrolled device
  // through Cloudflare Access — the server says so in words when it is not.
  const approve = useMutation({
    onMutate: () => { ctx.setPending(w.id, true); ctx.fail(w.id, null); },
    onSettled: () => ctx.setPending(w.id, false),
    mutationFn: () => api.approvePayoutAddress(w.id),
    onSuccess: (r) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      ctx.onNotice(r.already ? `${name} was already approved for payouts.` : `${name} is approved for payouts.`, w.id);
      ctx.close(w.id, 'approve');
      qc.invalidateQueries({ queryKey: ['wallets'] });
    },
    onError: (e) => { tg()?.HapticFeedback.notificationOccurred('error'); ctx.fail(w.id, errorWords(e)); },
  });
  const submit = () => change.mutate({ step, reason: form.reason, label: shownLabel(form, w) });
  const solid = (p: boolean) => `${p ? 'btn-primary' : 'btn-secondary'} w-full min-h-11 px-4 text-body disabled:opacity-50 lg:min-h-10 lg:w-auto`;

  return (
    <div className="space-y-3 border-t border-stroke pt-4" onFocusCapture={() => ctx.touch(w.id)} data-step={step}>
      {step === 'approve' && (
        <>
          <p className="text-meta">Once approved, the wallet can pay this address.</p>
          <button type="button" className={solid(primary)} disabled={busy || stale} onClick={() => approve.mutate()}>
            {busy ? 'Approving…' : 'Approve payout address'}
          </button>
        </>
      )}
      {step === 'label' && (
        <Field label="Label">
          <div className="flex gap-2">
            <input className="input min-w-0 flex-1 disabled:opacity-60" value={shownLabel(form, w)} disabled={busy}
              onChange={(e) => ctx.edit(w.id, { label: e.target.value }, w.label ?? '')} placeholder="A note for this address" />
            <button type="button" className={`${primary ? 'btn-primary' : 'btn-secondary'} min-h-11 px-4 text-body disabled:opacity-50 lg:min-h-10`} disabled={busy || stale} onClick={submit}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Field>
      )}
      {(step === 'retire' || step === 'archive') && (
        <>
          {/* The one-way step's words before it is taken: one line, the whole sentence behind Details (the operator's ruling, 26 Oct). */}
          <StateLine tone="warn" more={stepWords(step, w.chain)}>{stepLine(step, w.chain)}</StateLine>
          <Field label="Why" hint="at least 8 characters">
            <input className="input w-full disabled:opacity-60" value={form.reason} disabled={busy} onChange={(e) => ctx.edit(w.id, { reason: e.target.value })}
              placeholder={step === 'retire' ? 'e.g. November rotation' : 'e.g. nobody pays into it any more'} />
          </Field>
          {/* Archive is the step that makes the books blind to an address: red, as the reverse door's confirm is (components/ReverseEntry.tsx). */}
          <button type="button" className={step === 'archive' ? 'w-full min-h-11 rounded-lg bg-neg px-4 text-body text-white disabled:opacity-40 lg:min-h-10 lg:w-auto' : solid(primary)}
            disabled={form.reason.trim().length < 8 || busy || stale} onClick={submit}>
            {busy ? 'Saving…' : step === 'retire' ? 'Retire this address' : 'Archive — stop watching it'}
          </button>
        </>
      )}
      {stale && <p className="text-meta text-warn">{labelMoved ? 'The label changed — cancel this form.' : 'This address has changed — cancel this form.'}</p>}
      <ErrorLine>{form.error}</ErrorLine>
    </div>
  );
}
